/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import { View } from 'backbone.marionette';
import Radio from 'backbone.radio';
import Ladda from 'ladda';

import callByNeed from '../../util/callByNeed';

import SideNavContentCollectionView from './SideNavContentCollectionView';
import SideNavLinkCollectionView from './SideNavLinkCollectionView';
import tmplSideNavSectionView from './SideNavSectionView.hbs';
import './SideNavSectionView.css';

const SideNavSectionView = View.extend({
  template: tmplSideNavSectionView,

  className() {
    const baseClass = 'side-nav-section-view ps-2 pt-2';
    if (this.options.expanded) {
      return `${baseClass} side-nav-section-view-expanded`;
    }
    return baseClass;
  },

  ui: {
    linksContainer: '.links-container',
    buttonsContainer: '.buttons-container',
    contentsContainer: '.contents-container',
    previousButton: '.previous-button',
    nextButton: '.next-button',
    saveButton: '.save-button',
    submitButton: '.submit-button',
    submitHr: '.submit-hr',
    link: '.list-group-item'
  },

  regions: {
    linksContainer: '@ui.linksContainer',
    buttonsContainer: '@ui.buttonsContainer',
    contentsContainer: '@ui.contentsContainer'
  },

  events: {
    'click @ui.previousButton': 'onClickPrevious',
    'click @ui.nextButton': 'onClickNext',
    'click @ui.saveButton': 'onClickSave',
    'click @ui.submitButton': 'onClickSubmit'
  },

  childViewEvents: {
    'navlink:clicked': 'onChildNavLinkClick'
  },

  options: {
    contentWidth: 'full', // 'full' or 'medium'. Sets how much of the page the content should take up.
    useNavigationButtons: false, // show the next/previous buttons at the bottoms of each section
    expanded: false // show the sections all at once without navigation
  },

  templateContext() {
    const { contentWidth, useNavigationButtons, expanded, editable, title } = this.options;
    return { contentWidth, useNavigationButtons, expanded, editable, title };
  },

  onBeforeRender() {
    if (!_.includes(['medium', 'full'], this.options.contentWidth)) {
      throw new Error('The contentWidth option of SideNavSectionView must be either "full" or "medium"');
    }
  },

  onRender() {
    Radio.channel('sideNavSectionView').on('toggle:saveBtn', (state) => this.setSaveBtnShown(state));

    this.removeUnrenderableViewsFromCollection();
    this.showSideNavLinkCollectionView();
    this.showSideNavContentCollectionView();

    this.setNavigationButtonVisibility();
  },

  showSideNavLinkCollectionView() {
    if (!this.options.expanded) {
      this.linkCollectionView = new SideNavLinkCollectionView({
        collection: this.collection,
        activeSectionId: this.getDefaultActiveSectionId()
      });
      this.showChildView('linksContainer', this.linkCollectionView);
    }
  },

  showSideNavContentCollectionView() {
    this.contentCollectionView = new SideNavContentCollectionView({
      collection: this.collection,
      sideNavView: this,
      expanded: this.options.expanded,
      activeSectionId: this.getDefaultActiveSectionId(),
      migrated: this.model.get('migrated')
    });
    this.showChildView('contentsContainer', this.contentCollectionView);
  },

  async onClickNext() {
    const spinner = Ladda.create(this.ui.nextButton[0]);
    spinner.start();

    try {
      await this.onNavigateAway();

      const activeLinkIndex = parseInt(this.linkCollectionView.getActiveLinkIndex());
      const nextActiveLinkIndex = activeLinkIndex + 1;
      this.setActiveViewByIndex(nextActiveLinkIndex);
      this.setNavigationButtonVisibility();
      window.scrollTo(0, 0);
    } finally {
      spinner.stop();
    }
  },

  async onClickPrevious() {
    const spinner = Ladda.create(this.ui.previousButton[0]);
    spinner.start();

    try {
      await this.onNavigateAway();

      const activeLinkIndex = parseInt(this.linkCollectionView.getActiveLinkIndex());
      const nextActiveLinkIndex = activeLinkIndex - 1;
      this.setActiveViewByIndex(nextActiveLinkIndex);

      this.setNavigationButtonVisibility();
      window.scrollTo(0, 0);
    } finally {
      spinner.stop();
    }
  },

  async onNavigateAway(previousLinkIndex) {
    if (typeof previousLinkIndex === 'undefined') {
      previousLinkIndex = this.linkCollectionView.getActiveLinkIndex();
    }

    const previousModel = this.linkCollectionView.collection.at(previousLinkIndex);
    if (this.options.editable && previousModel.get('isSavable')) {
      const view = this.collection.at(previousLinkIndex).get('view');
      if (_.isFunction(view.call)) {
        return await view.call('onSave');
      } else if (_.isFunction(view.onSave)) {
        return await view.onSave();
      }
    }
  },

  async onClickSave(e) {
    e.preventDefault();
    const sectionId = this.linkCollectionView.getActiveLinkId();
    const view = this.getViewBySection(sectionId);

    const spinner = Ladda.create(e.currentTarget);
    spinner.start();

    try {
      if (_.isFunction(view.call)) {
        await view.call('onSave', e);
      } else if (_.isFunction(view.onSave)) {
        await view.onSave(e);
      }
    } finally {
      spinner.stop();
    }
  },

  async onClickSubmit(e) {
    e.preventDefault();
    const sectionId = this.linkCollectionView.getActiveLinkId();
    const view = this.getViewBySection(sectionId);

    const spinner = Ladda.create(e.currentTarget);
    spinner.start();

    try {
      if (_.isFunction(view.call)) {
        await view.call('onSave', e, true);
      } else if (_.isFunction(view.onSave)) {
        await view.onSave(e);
      }
    } catch {
      // Error (e.g., no onSave function) - Ignore
    }

    try {
      if (_.isFunction(view.call)) {
        await view.call('onSubmit', e);
      } else if (_.isFunction(view.onSubmit)) {
        await view.onSubmit(e);
      }
    } catch {
      // Error (e.g., no onSubmit function) - Ignore
    }

    await this.triggerMethod('submit', sectionId); // Trigger parent if submission is implemented there
    spinner.stop();
  },

  setSaveBtnShown(isShown) {
    if (this.ui.saveButton instanceof jQuery) {
      isShown ? this.ui.saveButton.show() : this.ui.saveButton.hide();
    } else {
      isShown ? $(this.ui.saveButton).show() : $(this.ui.saveButton).hide();
    }
  },

  async onChildNavLinkClick(data) {
    this.linkCollectionView.setLoadingIndex(data.linkIndex);
    try {
      await this.onNavigateAway(data.previousLinkIndex);
      this.setActiveViewByIndex(data.linkIndex);
      this.setNavigationButtonVisibility();
      window.scrollTo(0, 0);
    } finally {
      this.linkCollectionView.setLoadedIndex(data.linkIndex);
    }
  },

  getDefaultActiveSectionId() {
    return this.options.activeSectionId ? this.options.activeSectionId : this.collection.models[0].id;
  },

  setActiveViewByIndex(idx) {
    this.linkCollectionView.setActiveLinkByIndex(idx);
    this.contentCollectionView.setActiveLinkByIndex(idx);
  },

  setNavigationButtonVisibility() {
    if (!this.linkCollectionView) {
      return;
    }
    const activeLinkIndex = parseInt(this.linkCollectionView.getActiveLinkIndex());
    const activeModel = this.linkCollectionView.collection.at(activeLinkIndex);

    this.ui.submitButton.hide();
    this.ui.submitHr.hide();
    this.ui.saveButton.hide();

    if (activeModel.get('showSubmitButton')) {
      this.ui.submitButton.show();
      this.ui.submitHr.show();
    }

    if (activeModel.get('isSavable')) {
      this.ui.saveButton.show();
    }

    if (activeModel.get('hideNavigation')) {
      this.ui.nextButton.hide();
      this.ui.previousButton.hide();
    }

    if ((activeLinkIndex >= this.collection.length - 1) && !activeModel.get('hideNavigation')) {
      this.ui.nextButton.hide();
    }

    if ((activeLinkIndex < this.collection.length - 1) && !activeModel.get('hideNavigation')) {
      this.ui.nextButton.show();
    }

    if ((activeLinkIndex === 0) && !activeModel.get('hideNavigation')) {
      this.ui.previousButton.hide();
    }

    if ((activeLinkIndex > 0) && !activeModel.get('hideNavigation')) {
      this.ui.previousButton.show();
    }
  },

  /**
   * @summary will iterate over each section in the SideNav and validate each child view
   * @param {Object} config - The configuration object which tells each view how to validate
   * @param {string} config.type - The type of valdiation to perform. Should be 'save' or 'submit'
   */
  validateAll(config) {
    // If a config object is not passed in, set a default
    if (!config) {
      config = {
        type: 'submit'
      };
    }
    // If a config object is passed in, but type is not set, set its default value
    if (!config.type) {
      config.type = 'submit';
    }
    const validationPromises = this.collection.reduce((memo, sectionModel) => {
      const view = sectionModel.get('view');
      if (!view) {
        return memo;
      }
      if (view.validate && _.isFunction(view.validate)) {
        const validateView = view.validate.bind(view);
        const promise = validateView(config);
        promise
          .then(() => {
            sectionModel.trigger('no:errors', false);
          })
          .catch(() => {
            sectionModel.trigger('has:errors', true);
          });
        memo.push(promise);
        return memo;
      } else {
        return memo;
      }
    }, []);
    return Promise.all(validationPromises);
  },

  getFormData() {
    return this.collection.reduce((memo, sectionModel) => {
      const view = sectionModel.get('view');
      if (!view || !sectionModel.id) {
        return memo;
      }
      if (view.getFormData && _.isFunction(view.getFormData)) {
        const getFormDataResults = view.getFormData();
        if (getFormDataResults) {
          memo[sectionModel.id] = getFormDataResults;
        }
      }
      return memo;
    }, {});
  },

  getFormDataBySection(sectionId) {
    const sectionViewModel = this.collection.find((sectionModel) => {
      if (sectionModel.id === sectionId) {
        return true;
      }
    });
    const sectionView = sectionViewModel.get('view') || {};
    if (sectionView.getFormData && _.isFunction(sectionView.getFormData)) {
      return sectionView.getFormData();
    }
  },

  getViewBySection(sectionId) {
    const sectionViewModel = this.collection.find((sectionModel) => {
      if (sectionModel.id === sectionId) {
        return true;
      }
    });
    const sectionView = sectionViewModel.get('view') || {};
    return sectionView;
  },

  removeUnrenderableViewsFromCollection() {
    const renderableModels = this.collection.filter((model) => {
      if (model.has('renderable')) {
        return callByNeed(model.get('renderable'));
      } else {
        return true;
      }
    });
    this.collection.reset(renderableModels);
  }
});

export default SideNavSectionView;
